import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useSubscription } from '@apollo/react-hooks';

import { getAGGClient, markErrorAsHandled } from '@confluence/graphql';
import { getConfluenceContentAri } from '@confluence/content-types-utils';
import { useSessionData } from '@confluence/session-data';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { Attribution, getStatusCode } from '@confluence/error-boundary';

const subscription = gql`
	subscription ContentTypesTitleSubscription($id: ID!) {
		confluence_onContentModified(id: $id) {
			type
			id
			__typename
			_deltas
			contentTitleUpdated {
				contentTitle
				__typename
			}
		}
	}
`;

// exported for testing purposes only
export const onSubscriptionData = (
	response: any,
	onDataReceived: (data: string) => void,
	contentId: string,
	contentType: string,
) => {
	getAnalyticsWebClient().then((client) => {
		client.sendOperationalEvent({
			action: 'received',
			actionSubject: 'subscription.event',
			actionSubjectId: 'contentTitleUpdated',
			source: 'contentTypesTitleSubscription',
			attributes: {
				contentId,
				contentType,
			},
		});
	});

	const data = response?.subscriptionData?.data;
	const _deltas = data?.confluence_onContentModified?._deltas;
	if (!data || !Array.isArray(_deltas)) {
		return;
	}
	if (_deltas?.length === 0 || _deltas.indexOf('contentTitleUpdated') === -1) {
		return;
	}
	const { contentTitleUpdated } = data?.confluence_onContentModified;
	const title = contentTitleUpdated?.contentTitle;

	onDataReceived(title);
};
export const ContentTypesTitleSubscription = ({
	contentType,
	contentId,
	onDataReceived,
	skip = false,
}: {
	contentType: string;
	contentId: string;
	onDataReceived: (data: string) => void;
	skip?: boolean;
}) => {
	const { activationId, cloudId } = useSessionData();
	const canIgnoreError = ({ statusCode }: { statusCode?: number }) => {
		if (statusCode === 403) {
			return true;
		}
		return false;
	};

	const ari = getConfluenceContentAri({
		siteId: cloudId,
		resourceType: contentType,
		activationId,
		resourceId: contentId,
	});

	const { error } = useSubscription(subscription, {
		variables: {
			id: ari,
		},
		onSubscriptionData(response) {
			onSubscriptionData(response, onDataReceived, contentId, contentType);
		},
		skip: skip || !ari || !contentId || !contentType || !activationId,
		client: getAGGClient(),
	});

	useEffect(() => {
		if (error) {
			const errorMessage = error?.message || (error as any)?.errors?.[0]?.message; // error object can be an array
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (!canIgnoreError({ statusCode })) {
				const e = new Error(`Subscription Error - ContentTypesTitleSubscription:${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}
	}, [error, contentId, contentType]);

	return null;
};
